<template>
  <section
    v-if="data?.data?.length"
    class="categorized-articles"
  >
    <div class="f mb32">
      <h2>{{ category?.title }}</h2>

      <nuxt-link
        :to="localePath(`/blog/${category?.link}`)"
        class="btn btn-primary col-primary"
      >
        {{ $t('see_all', { count: category?.posts_count }) }}
      </nuxt-link>
    </div>
    <PagesBlogCardsGrid>
      <PagesBlogArticleCard
        v-for="article in data?.data"
        :key="article"
        :article="article as BlogPost"
      />
    </PagesBlogCardsGrid>
  </section>
</template>

<script lang="ts" setup>
import type { BlogCategory, BlogPost } from '~/graphql/generated/schema-types';
import { languagesEnum } from '~/types/LocalesToCockies';
import { getBlogPosts } from '~/utils/api';

const props = defineProps<{
  category: BlogCategory
}>();

const emit = defineEmits(['onPostsLoaded']);
const config = useRuntimeConfig();
const siteHost = config.public.siteHost;
const localePath = useLocalePath();
const { locale } = useI18n();

const { data, error } = await useAsyncData(`getBlogPostsByCategory-${props.category?.link}`, () => getBlogPosts({
  category: props?.category?.link,
  hidden: false,
  is_main: false,
  page: 1,
  limit: 3,
}), {
  transform: input => input?.data?.getBlogPosts,
});

if (error.value) {
  throw createError({ status: 404 });
}

function getSchemasFromPosts(posts: BlogPost[]) {
  return posts.filter(art => art.lang === languagesEnum[locale.value.toUpperCase() as keyof typeof languagesEnum]).map(post => ({
    '@type': 'NewsArticle',
    'headline': post?.title,
    'url': `${siteHost}/blog/${post?.category?.link}/${post?.link}`,
    'image': post?.img,
    'datePublished': post?.published_at,
    'dateModified': post?.updated_at,
    'author': {
      '@type': 'Person',
      'name': post?.author?.persona_name,
    },
  }));
}

emit('onPostsLoaded', getSchemasFromPosts(data.value?.data as BlogPost[] || []));
</script>

<style lang="scss" scoped>
.categorized-articles {
  @media screen and (max-width: 768px) {
    &:deep(.cards-grid) {
      display: flex;
      overflow-x: auto;

      .article-card {
        min-width: 80vw;
      }
    }
  }
}
</style>
